import { useQuery } from '@tanstack/react-query';
import { ApiResponse } from 'apisauce';
import { capitalize } from 'lodash';
import {
    AutocompleteElement,
    SelectElement,
    useFormContext,
} from 'react-hook-form-mui';

import Api from '@barboxx/api';
import {
    Ingredient,
    Pagination,
    Product,
    ProductSubtype,
    ProductType,
} from '@barboxx/models/types';
import {
    DRINK_RECIPE_ITEM_TYPE,
    DrinkRecipeItemType,
    FLAVOR_TYPES,
} from '@barboxx/types';
import { enumToFormOptions, enumToList } from '@barboxx/utils';

type FieldType = 'target' | 'substitution';

type PossibleType = ProductType | ProductSubtype | Ingredient | Product;

interface Props {
    disabled?: boolean;
    type: FieldType;
}

interface SubstitutionIdWatchProps {
    targetId?: number;
    substitutionId?: number;
}
interface SubstitutionTypeWatchProps {
    targetType?: DrinkRecipeItemType;
    substitutionType?: DrinkRecipeItemType;
}

function getLabel(type: FieldType) {
    switch (type) {
        case 'target':
            return 'Target';
        case 'substitution':
            return 'Substitution';
    }
}

function getApiCall(type: DrinkRecipeItemType) {
    switch (type) {
        case 'ProductType':
            return Api.Products.Types.getProductTypes;
        case 'ProductSubtype':
            return Api.Products.Subtypes.getProductSubtypes;
        case 'Ingredient':
            return Api.Core.Ingredient.getIngredients;
        case 'Product':
            return Api.Products.Products.getProducts;
    }
}

const INITIAL_DATA: ApiResponse<Pagination<PossibleType>> = {
    ok: true,
    problem: null,
    originalError: null,
    data: {
        results: [],
        total: 1,
        isPaginated: false,
    },
};

export function SubstitutionIdInput({ disabled = false, type }: Props) {
    const formContext = useFormContext<
        SubstitutionIdWatchProps & SubstitutionTypeWatchProps
    >();
    const values = formContext.getValues();
    const { defaultValues } = formContext.formState;

    const selectedType =
        values?.[`${type}Type`] || defaultValues?.[`${type}Type`];

    const typeQueryResult = useQuery({
        queryKey: [selectedType],
        enabled: !!selectedType,
        refetchOnMount: true,
        queryFn: () =>
            selectedType
                ? getApiCall(selectedType)({
                      page: 0,
                      size: 1000,
                      sort: 'name,asc',
                  })
                : Promise.resolve(INITIAL_DATA),
    });

    return (
        <>
            <SelectElement
                fullWidth
                name={`${type}Type` as keyof SubstitutionTypeWatchProps}
                disabled={disabled}
                label={`${capitalize(type)} Type`}
                options={enumToFormOptions(DRINK_RECIPE_ITEM_TYPE)}
                type="string"
                onChange={value => {
                    formContext.setValue(
                        `${type}Id` as keyof SubstitutionIdWatchProps,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        null as any
                    );
                    if (value) {
                        typeQueryResult.refetch();
                    }
                }}
            />
            <AutocompleteElement
                label={`${getLabel(type)} Id`}
                name={`${type}EntityId`}
                loading={typeQueryResult.isLoading}
                multiple={false}
                options={typeQueryResult.data?.data?.results ?? []}
                transform={{
                    output(event, value, reason, details) {
                        if (reason === 'selectOption') {
                            return typeof value === 'string'
                                ? value
                                : value?.id;
                        }
                        if (reason === 'removeOption') {
                            return null;
                        }
                        return value;
                    },
                }}
                autocompleteProps={{
                    fullWidth: true,
                    readOnly: disabled,
                    disabled: disabled,
                    isOptionEqualToValue(
                        option?: PossibleType,
                        value?: PossibleType | number | null
                    ) {
                        const isNumber = typeof value === 'number';

                        if (isNumber) {
                            return option?.id === value;
                        } else {
                            return option?.id === (value?.id ?? value);
                        }
                    },
                    getOptionLabel(option) {
                        if (typeof option === 'string') {
                            return option;
                        }
                        return getSubstitutionOptionLabel(option, selectedType);
                    },
                    getOptionKey(option) {
                        if (typeof option === 'string') {
                            return option;
                        }
                        return `${option?.id}`;
                    },
                }}
            />
            {(selectedType === DRINK_RECIPE_ITEM_TYPE.ProductType ||
                selectedType === DRINK_RECIPE_ITEM_TYPE.ProductSubtype) && (
                <AutocompleteElement
                    label={`${getLabel(type)} Flavor`}
                    name={`${type}Flavor`}
                    options={enumToList(FLAVOR_TYPES)}
                    autocompleteProps={{
                        fullWidth: true,
                        readOnly: disabled,
                        disabled: disabled,
                    }}
                />
            )}
        </>
    );
}

export function getSubstitutionOptionLabel(
    option: PossibleType,
    selectedType?: DrinkRecipeItemType
) {
    switch (selectedType) {
        case 'Product': {
            const product = option as Product;
            return `${product?.brand?.name} - ${product.name}`;
        }

        case 'ProductSubtype': {
            const productSubtype = option as ProductSubtype;
            return `${productSubtype?.productType?.name} - ${productSubtype.name}`;
        }
        case 'ProductType':
        case 'Ingredient':
        default:
            return option.name;
    }
}
